<template>
  <div class="age-ei-level-grouped-chart">
    <h3 class="age-ei-level-grouped-chart__title">
      {{ title }}
    </h3>

    <div class="age-ei-level-grouped-chart__scroll">
      <div class="age-ei-level-grouped-chart__inner">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
)

const DEFAULT_BAR_COLOR = '#D5DBE2'
const ACTIVE_BAR_COLOR = '#99DA6E'

const valueLabelPlugin = {
  id: 'ageValueLabelPlugin',

  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)

    ctx.save()
    ctx.font = '600 12px sans-serif'
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    meta.data.forEach((bar, index) => {
      const value = chart.data.datasets[0].data[index]

      if (value === null || value === undefined) {
        return
      }

      const isZero = Number(value) === 0
      const centerY = bar.y + ((bar.base - bar.y) / 2)

      ctx.fillStyle = isZero ? 'rgba(0, 0, 0, 0.45)' : '#ffffff'
      ctx.fillText(String(value), bar.x, isZero ? bar.base - 8 : centerY)
    })

    ctx.restore()
  },
}

const levelLabelPlugin = {
  id: 'ageLevelLabelPlugin',

  afterDraw(chart, args, pluginOptions) {
    const groups = pluginOptions?.groups || []
    const xScale = chart.scales.x

    if (!groups.length || !xScale) {
      return
    }

    const { ctx } = chart

    ctx.save()
    ctx.font = '400 11px sans-serif'
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'

    groups.forEach((group) => {
      const first = xScale.getPixelForTick(group.startIndex)
      const last = xScale.getPixelForTick(group.endIndex)
      const center = first + ((last - first) / 2)

      /**
       * xScale.bottom is already below the age labels,
       * so this puts the level label on its own line.
       */
      ctx.fillText(group.label, center, xScale.bottom + 8)
    })

    ctx.restore()
  },
}

export default {
  name: 'AgeEiLevelGroupedChart',

  props: {
    title: {
      type: String,
      default: 'Сравнение на каждом уровне Общего ЭИ',
    },

    levels: {
      type: Array,
      default: () => [],
    },

    maxValue: {
      type: Number,
      default: 35,
    },

    defaultColor: {
      type: String,
      default: DEFAULT_BAR_COLOR,
    },

    activeColor: {
      type: String,
      default: ACTIVE_BAR_COLOR,
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartInstance = ref(null)

    const flatItems = computed(() => {
      const result = []

      props.levels.forEach((level) => {
        const items = Array.isArray(level.items) ? level.items : []

        items.forEach((item) => {
          result.push({
            levelKey: level.key,
            levelLabel: level.label,
            key: item.key,
            label: item.label,
            value: toNumber(item.value),
            color: item.color || props.defaultColor,
          })
        })
      })

      return result
    })

    const levelGroups = computed(() => {
      const result = []
      let offset = 0

      props.levels.forEach((level) => {
        const count = Array.isArray(level.items) ? level.items.length : 0

        if (!count) {
          return
        }

        result.push({
          key: level.key,
          label: level.label,
          startIndex: offset,
          endIndex: offset + count - 1,
        })

        offset += count
      })

      return result
    })

    const destroyChart = () => {
      if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
      }
    }

    const renderChart = () => {
      destroyChart()

      if (!chartCanvas.value) {
        return
      }

      const labels = flatItems.value.map((item) => item.label)
      const values = flatItems.value.map((item) => item.value)
      const colors = flatItems.value.map((item) => item.color)

      chartInstance.value = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderRadius: 4,
              borderSkipped: false,
              categoryPercentage: 0.96,
              barPercentage: 0.92,
              minBarLength: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          layout: {
            padding: {
              top: 6,
              right: 4,
              bottom: 52,
              left: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                title: (items) => {
                  const index = items?.[0]?.dataIndex
                  const item = flatItems.value[index]

                  return item ? `${item.levelLabel}: ${item.label}` : ''
                },
                label: (context) => {
                  return `${context.parsed.y} чел.`
                },
              },
            },
            ageLevelLabelPlugin: {
              groups: levelGroups.value,
            },
          },
          scales: {
            x: {
              offset: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                autoSkip: false,
                color: 'rgba(0, 0, 0, 0.45)',
                padding: 4,
                font: {
                  size: 11,
                  weight: 400,
                },
                maxRotation: 0,
                minRotation: 0,
                callback(value) {
                  return String(this.getLabelForValue(value) || '')
                },
              },
              border: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
              max: props.maxValue,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                precision: 0,
                stepSize: 5,
                color: 'rgba(0, 0, 0, 0.6)',
                font: {
                  size: 13,
                  weight: 400,
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
        plugins: [valueLabelPlugin, levelLabelPlugin],
      })
    }

    onMounted(() => {
      nextTick(renderChart)
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [
          props.levels,
          props.maxValue,
          props.defaultColor,
          props.activeColor,
        ],
        () => {
          nextTick(renderChart)
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
    }
  },
}

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.age-ei-level-grouped-chart {
  width: 100%;
}

.age-ei-level-grouped-chart__title {
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.age-ei-level-grouped-chart__scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.age-ei-level-grouped-chart__inner {
  width: 100%;
  min-width: 880px;
  height: 300px;
}

@media (max-width: $tablet-breakpoint) {
  .age-ei-level-grouped-chart__title {
    font-size: 15px;
    margin-bottom: 16px;
  }

  .age-ei-level-grouped-chart__inner {
    min-width: 840px;
    height: 290px;
  }
}

@media (max-width: $mobile-breakpoint) {
  .age-ei-level-grouped-chart__title {
    font-size: 15px;
  }

  .age-ei-level-grouped-chart__inner {
    min-width: 840px;
    height: 280px;
  }
}
</style>
